import { Timestamp } from "firebase/firestore";
import { repo } from "../dbservice/repo";

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Safely converts any timestamp-like value to a JS Date.
 * Handles Firestore Timestamps, JS Dates, ISO strings, and epoch numbers.
 */
const safeToDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value === "string") {
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  if (typeof value === "number") return new Date(value);
  return null;
};

/**
 * Validates that a value is not empty / null / undefined / the literal
 * strings "undefined" or "null".
 */
const isBlank = (value) =>
  value === undefined ||
  value === null ||
  value === "undefined" ||
  value === "null" ||
  (typeof value === "string" && value.trim() === "");

// ─────────────────────────────────────────────────────────────────────────────
// PUBLIC API
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Generates a cryptographically-random 32-char hex token, persists the token
 * document via the Repository layer (local-first → cloud backup), and returns
 * the plain token string.
 *
 * @param {string} assetId
 * @param {string} userId
 * @param {string} companyId
 * @returns {Promise<string>} token
 */
export const generateQRToken = async (assetId, userId, companyId) => {
  try {
    // ── 1. Parameter validation ──────────────────────────────────────────────
    const params = { assetId, userId, companyId };
    Object.entries(params).forEach(([key, val]) => {
      if (isBlank(val)) {
        throw new Error(
          `[QR TOKEN] Required parameter '${key}' is missing or empty.`,
        );
      }
    });

    const cleanAssetId = String(assetId).trim();
    const cleanUserId = String(userId).trim();
    const cleanCompanyId = String(companyId).trim();

    console.log("[QR TOKEN] Parameters validated:", {
      assetId: cleanAssetId,
      userId: cleanUserId,
      companyId: cleanCompanyId,
    });

    // ── 2. Generate token ────────────────────────────────────────────────────
    const token = crypto
      .getRandomValues(new Uint8Array(16))
      .reduce((str, byte) => str + byte.toString(16).padStart(2, "0"), "");

    const expiresAtDate = new Date();
    expiresAtDate.setFullYear(expiresAtDate.getFullYear() + 10); // 10-year TTL

    // ── 3. Build document payload ────────────────────────────────────────────
    // 'id' is mandatory so the repo layer can derive the Firestore docId.
    const docId = `${token}_${cleanAssetId}`;

    const tokenData = {
      id: docId,
      assetId: cleanAssetId,
      token: token,
      createdBy: cleanUserId,
      companyId: cleanCompanyId,
      expiresAt: Timestamp.fromDate(expiresAtDate),
      isActive: true,
      createdAt: Timestamp.fromDate(new Date()),
    };

    // ── 4. Deep validation – catch any accidental blanks ─────────────────────
    Object.entries(tokenData).forEach(([key, value]) => {
      // Timestamps are objects; skip the string-blank check for them.
      if (value instanceof Timestamp) return;
      if (isBlank(value)) {
        throw new Error(
          `[QR TOKEN] Field '${key}' resolved to an invalid value: "${value}"`,
        );
      }
    });

    console.log("[QR TOKEN] Payload validated. Writing via repo layer...");

    // ── 5. Persist via repo (local-first → cloud backup) ─────────────────────
    await repo.upsertData("publicAssetTokens", docId, tokenData);

    console.log(
      "[QR TOKEN] ✅ Token saved successfully via repo layer:",
      token,
    );
    return token;
  } catch (error) {
    console.error("[QR TOKEN] ❌ generateQRToken failed:", error);
    throw error;
  }
};

/**
 * Validates a token by fetching the token document via the Repository layer.
 * Returns true only when the document exists, isActive, and has not expired.
 *
 * @param {string} token
 * @param {string} assetId
 * @returns {Promise<boolean>}
 */
export const validateQRToken = async (token, assetId) => {
  try {
    if (isBlank(token) || isBlank(assetId)) {
      console.error(
        "[QR TOKEN] validateQRToken – token or assetId is missing.",
      );
      return false;
    }

    const docId = `${token}_${assetId}`;

    // ── Fetch via repo layer ─────────────────────────────────────────────────
    const tokenData = await repo.fetchById("publicAssetTokens", docId);

    if (!tokenData) {
      console.error("[QR TOKEN] Token document not found:", docId);
      return false;
    }

    const isActive = tokenData.isActive === true;

    // repo may return raw Firestore Timestamp objects OR plain JS objects
    // depending on whether the data came from local Supabase or Firestore.
    const expiresAt = safeToDate(tokenData.expiresAt);
    const notExpired = expiresAt ? expiresAt > new Date() : false;

    console.log("[QR TOKEN] Validation result:", {
      isActive,
      notExpired,
      expiresAt,
    });
    return isActive && notExpired;
  } catch (error) {
    console.error("[QR TOKEN] ❌ validateQRToken failed:", error);
    return false;
  }
};

/**
 * Revokes a token by setting isActive = false via the Repository layer.
 *
 * @param {string} token
 * @param {string} assetId
 * @returns {Promise<boolean>}
 */
export const revokeQRToken = async (token, assetId) => {
  try {
    if (isBlank(token) || isBlank(assetId)) {
      throw new Error(
        "[QR TOKEN] revokeQRToken – token and assetId are required.",
      );
    }

    const docId = `${token}_${assetId}`;

    await repo.upsertData("publicAssetTokens", docId, {
      id: docId,
      isActive: false,
      revokedAt: Timestamp.fromDate(new Date()),
    });

    console.log("[QR TOKEN] ✅ Token revoked via repo layer:", token);
    return true;
  } catch (error) {
    console.error("[QR TOKEN] ❌ revokeQRToken failed:", error);
    return false;
  }
};

/**
 * Convenience wrapper that accepts the full `currentUser` context object
 * instead of individual ids. Maintains backward compatibility with all
 * existing call-sites.
 *
 * @param {string} assetId
 * @param {{ uid: string, companyId: string }} userContext
 * @returns {Promise<string>} token
 */
export const generateQRTokenFromUser = async (assetId, userContext) => {
  if (!userContext) {
    throw new Error(
      "[QR TOKEN] generateQRTokenFromUser – userContext is required.",
    );
  }
  if (isBlank(userContext.uid)) {
    throw new Error(
      "[QR TOKEN] generateQRTokenFromUser – userContext.uid is missing.",
    );
  }
  if (isBlank(userContext.companyId)) {
    throw new Error(
      "[QR TOKEN] generateQRTokenFromUser – userContext.companyId is missing.",
    );
  }

  return generateQRToken(assetId, userContext.uid, userContext.companyId);
};
